import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

const initialState = {
  transitionForms: [],
  currentTransitionForm: null,
  isLoading: false,
  error: null,
};

export const fetchTransitionForms = createAsyncThunk(
  'transitionForm/fetchAll',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId ? `/transition-forms/${patientId}` : '/transition-forms';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch Transition Form');
    }
  }
);

export const fetchTransitionForm = createAsyncThunk(
  'transitionForm/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/transition-forms/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch Transition Form');
    }
  }
);

export const createTransitionForm = createAsyncThunk(
  'transitionForm/create',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/transition-forms', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(error.response?.data?.error || 'Failed to create Transition Form');
    }
  }
);

export const updateTransitionForm = createAsyncThunk(
  'transitionForm/update',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/transition-forms/${id}`, formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(error.response?.data?.error || 'Failed to update Transition Form');
    }
  }
);

export const deleteTransitionForm = createAsyncThunk(
  'transitionForm/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/transition-forms/${id}`);
      return { id, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete Transition Form');
    }
  }
);

const slice = createSlice({
  name: 'transitionForm',
  initialState,
  reducers: {
    clearCurrentTransitionForm: (state) => { state.currentTransitionForm = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransitionForms.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(fetchTransitionForms.fulfilled, (s, a) => { s.isLoading = false; s.transitionForms = a.payload; })
      .addCase(fetchTransitionForms.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(fetchTransitionForm.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(fetchTransitionForm.fulfilled, (s, a) => { s.isLoading = false; s.currentTransitionForm = a.payload; })
      .addCase(fetchTransitionForm.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(createTransitionForm.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(createTransitionForm.fulfilled, (s, a) => { s.isLoading = false; s.transitionForms.unshift(a.payload); })
      .addCase(createTransitionForm.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(updateTransitionForm.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(updateTransitionForm.fulfilled, (s, a) => {
        s.isLoading = false;
        s.currentTransitionForm = a.payload;
        const i = s.transitionForms.findIndex((r) => r.id === a.payload.id);
        if (i !== -1) s.transitionForms[i] = a.payload;
      })
      .addCase(updateTransitionForm.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(deleteTransitionForm.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(deleteTransitionForm.fulfilled, (s, a) => { s.isLoading = false; s.transitionForms = s.transitionForms.filter((r) => r.id !== a.payload.id); })
      .addCase(deleteTransitionForm.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; });
  },
});

export const { clearCurrentTransitionForm, clearError } = slice.actions;
export default slice.reducer;
